import { css, SerializedStyles } from '@emotion/react'
import { useTheme } from 'bold-ui'
import { red } from 'bold-ui/lib/styles/colors'
import { Color } from 'csstype'

import { useBadgeConfig } from './useBadgeConfig'

interface BaseBadgeProps {
  style?: {
    position?: SerializedStyles
    wrapper?: SerializedStyles
    border?: (isHovered: boolean) => SerializedStyles
  }
  children?: JSX.Element
  color?: Color
  isHovered?: boolean
}

interface BadgeAutoProps extends BaseBadgeProps {
  type: 'auto'
  value: number | string | boolean
  maxLimit?: number
}

interface BadgeSmallProps extends BaseBadgeProps {
  type: 'small'
  value: boolean
}

interface BadgeLargeProps extends BaseBadgeProps {
  type: 'large'
  value: number | string
  maxLimit?: number
}

export type BadgeProps = BadgeAutoProps | BadgeSmallProps | BadgeLargeProps

export const BadgeNotificacao = (props: BadgeProps) => {
  const { isHovered, style, children, color = red.c50 } = props

  const theme = useTheme()
  const { finalColor, isLarge, isOverPictograma, displayValue, shouldRenderBadge } = useBadgeConfig(props, color)

  if (!shouldRenderBadge) {
    return children ?? null
  }

  const styles = createStyles(theme, finalColor, isHovered)
  const typeStyle = isLarge ? styles.large : styles.small
  const typedBorderStyle = isLarge ? styles.largePictogramaBorder : styles.smallPictogramaBorder
  const borderStyle = style?.border ? style.border(isHovered) : typedBorderStyle
  const badgeContent = <div css={[styles.badge, typeStyle]}>{displayValue}</div>

  return (
    <div css={[style?.wrapper ?? styles.wrapper]}>
      {isOverPictograma ? (
        <div css={[styles.pictogramaWrapper]}>
          <div css={[style?.position ?? styles.positionStyles, styles.pictograma, borderStyle]}>{badgeContent}</div>
          {children}
        </div>
      ) : (
        badgeContent
      )}
    </div>
  )
}

const createStyles = (theme, finalColor: string, isHovered: boolean) => {
  return {
    wrapper: css`
      display: inline-flex;
      align-items: center;
      position: relative;
    `,
    pictogramaWrapper: css`
      position: relative;
      display: inline-block;
      line-height: 0;
    `,
    positionStyles: css`
      top: -8px;
      left: 100%;
      transform: translate(-50%, 0);
    `,
    badge: css`
      background-color: ${finalColor};
      border-radius: 12px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
    `,
    large: css`
      padding: 2px 4px;
      height: 16px;
      min-width: 16px;
      color: ${theme.pallete.surface.main};
      font-size: 10px;
      font-weight: bold;
    `,
    small: css`
      width: 8px;
      height: 8px;
    `,
    pictograma: css`
      margin: -2px;
      display: inline-flex;
      position: absolute;
      border-radius: 12px;
    `,
    smallPictogramaBorder: css`
      border: 2px solid transparent;
    `,
    largePictogramaBorder: css`
      border: 2px solid ${isHovered ? theme.pallete.gray.c90 : theme.pallete.surface.main};
    `,
  }
}
